import { apiDelete, apiGet, apiPatch, apiPost, apiPut, apiUpload } from "./client";
import { isPreviewAuthEnabled } from "../features/auth/previewAuth";

function dataUrlToFile(dataUrl, filename = "photo.jpg") {
  const [header, base64] = dataUrl.split(",");
  const mimeMatch = header.match(/data:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}

export async function uploadProfileImage(source) {
  const file = typeof source === "string"
    ? dataUrlToFile(source)
    : source;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("folder", "profile");

  const result = await apiUpload("/uploads/image", formData);
  return {
    url: result.url,
    storageKey: result.key,
  };
}

export async function uploadProfileImages(sources = []) {
  const uploaded = [];

  for (const source of sources) {
    if (!source) continue;

    if (typeof source === "string" && !source.startsWith("data:")) {
      uploaded.push({ url: source });
      continue;
    }

    try {
      const result = await uploadProfileImage(source);
      uploaded.push(result);
    } catch (error) {
      if (isPreviewAuthEnabled() && typeof source === "string") {
        uploaded.push({ url: source });
        continue;
      }
      throw error;
    }
  }

  return uploaded;
}

export function profileToApiPayload(profile, { onboardingComplete = false } = {}) {
  const username = profile.username == null ? "" : String(profile.username).trim();
  const payload = {
    age: profile.age,
    bio: profile.bio || profile.desc || "",
    country: profile.country || "",
    city: profile.city || "",
    latitude: profile.latitude ?? profile.lat ?? null,
    longitude: profile.longitude ?? profile.lng ?? null,
    profileTypes: profile.profileTypes || (profile.category ? [profile.category] : []),
    languages: profile.languages || [],
    services: profile.services || [],
    serviceFor: profile.serviceFor || [],
    contact: {
      whatsapp: profile.contact?.whatsapp || "",
      phone: profile.contact?.phone || "",
      webcamEnabled: Boolean(profile.contact?.webcamEnabled),
    },
    isVisible: profile.isVisible ?? true,
    settings: profile.settings || {},
  };

  if (username) {
    payload.username = username;
  }

  if (onboardingComplete) {
    payload.onboardingComplete = true;
  }

  return payload;
}

export async function fetchMyProfile() {
  const data = await apiGet("/profiles/me");
  return data.profile;
}

export async function upsertMyProfile(profile, options = {}) {
  const payload = profileToApiPayload(profile, options);
  const data = await apiPut("/profiles/me", payload);
  return data.profile;
}

export async function patchMyProfile(patch) {
  const data = await apiPatch("/profiles/me", patch);
  return data.profile;
}

export async function fetchProfileById(profileId) {
  const data = await apiGet(`/profiles/${profileId}`);
  return data.profile;
}

export async function checkUsernameAvailability(username) {
  return apiGet("/profiles/check-username", { params: { username } });
}

export async function replaceMyPhotos(photos) {
  const data = await apiPut("/profiles/me/photos", { photos });
  return data.profile;
}

export async function addMyPhoto(photo) {
  const data = await apiPost("/profiles/me/photos", photo);
  return data.photo;
}

export async function deleteMyPhoto(photoId) {
  return apiDelete(`/profiles/me/photos/${photoId}`);
}
